/**
 * SE-6490 / SE-6866 — the pipeline card footer's compact "updated" label.
 * @remarks
 * The Player Card v7 design's footer reads `Updated 2d ago · Hugo`. The repo's
 * existing relative-time helpers render the long form (`3 days ago`,
 * `Yesterday` — {@link "@/utils/formatDateBasedOnDays".formatTimeRelative}),
 * which is the right shape for a report header and the wrong shape for a
 * 240px-wide card footer that must not wrap. This module owns the compact
 * form, as a pure function so the boundaries are unit-tested in one place and
 * the renderer stays presentational.
 *
 * **SE-6490 shipped the time alone; SE-6866 completed the sentence.** The
 * design always paired the timestamp with the person who made the change, but
 * `KanbanCard` exposed only `createdBy`/`creator` — who ADDED the player — so
 * naming them as the updater would have stated something false, and SE-6490's
 * criteria sanctioned shipping the relative time by itself. `KanbanCard` now
 * carries `lastEditedByUserId`, and {@link formatCardUpdatedFooter} composes
 * the two halves.
 *
 * The composition has one asymmetry worth stating, because it is a decision
 * and not an accident: a MISSING TIMESTAMP hides the whole row even when an
 * editor is known. `Hugo` on its own is not a fact about recency, which is
 * what this footer exists to convey; a missing or unresolvable EDITOR degrades
 * to the time alone, which still is.
 *
 * Known limit, in the schema rather than here: automation bumps `updatedAt`
 * but never `lastEditedByUserId` (the backend sets it from human edits only),
 * so a card can read `Updated 2h ago · Hugo` where the 2-hour-old change was a
 * rule and Hugo's edit is older. Naming the last human editor beside the last
 * change time is the only composition the schema permits.
 * @module features/player-kanban/utils/cardUpdatedAgo
 */

/** Milliseconds in one minute. */
const MS_PER_MINUTE = 60_000;

/** Milliseconds in one hour. */
const MS_PER_HOUR = 3_600_000;

/** Milliseconds in one 24-hour block. */
const MS_PER_DAY = 86_400_000;

/**
 * Whole days after which the label switches from days to months.
 * @remarks
 * 30 rather than 31 so the boundary is the same every month; the card is
 * conveying recency, not an exact date.
 */
const DAYS_PER_MONTH = 30;

/**
 * Separator between the elapsed time and the editor's name (SE-6866).
 * @remarks
 * The design's middle dot with hair spacing on both sides, not a hyphen or a
 * comma — it reads as "and also", which is what the two halves are.
 */
const FOOTER_SEPARATOR = " · ";

/**
 * Format a timestamp as the compact elapsed label, with no leading verb.
 * @remarks
 * The bare label the card footer prefixes with `Updated ` and the Table's
 * Activity column (SE-6554, Figma 80:15170) renders on its own — the design
 * writes `2h ago` / `today` under an `Activity` header, where a repeated
 * "Updated" in every cell would be noise.
 *
 * Boundaries are floored elapsed blocks, so the result is independent of the
 * viewer's timezone and of any offset encoded in the timestamp — the same
 * derivation {@link "@/features/player-kanban/utils/stageTime".computeDaysInStage}
 * uses. A future timestamp (clock skew) clamps to `just now` rather than
 * rendering a negative age. An absent or unparseable timestamp yields `null`,
 * so the caller can render its own "no value" cell rather than an empty one.
 * @param updatedAt - ISO timestamp of the card's last update.
 * @param now - Reference instant in epoch ms (defaults to `Date.now()`; injectable for tests).
 * @returns The compact label (e.g. `2d ago`), or `null` when unknown.
 */
export const formatRelativeAgo = (
  updatedAt: string | null | undefined,
  now: number = Date.now()
): string | null => {
  if (!updatedAt) return null;
  const updated = Date.parse(updatedAt);
  if (Number.isNaN(updated)) return null;

  const elapsed = now - updated;
  if (elapsed < MS_PER_MINUTE) return "just now";

  if (elapsed < MS_PER_HOUR) {
    return `${Math.floor(elapsed / MS_PER_MINUTE)}m ago`;
  }

  if (elapsed < MS_PER_DAY) {
    return `${Math.floor(elapsed / MS_PER_HOUR)}h ago`;
  }

  const days = Math.floor(elapsed / MS_PER_DAY);
  if (days < DAYS_PER_MONTH) return `${days}d ago`;

  return `${Math.floor(days / DAYS_PER_MONTH)}mo ago`;
};

/**
 * Format a card's `updatedAt` as the footer's compact relative label.
 * @remarks
 * The footer's wording is {@link formatRelativeAgo} plus the `Updated ` verb;
 * both surfaces therefore cross the same boundaries at the same instants,
 * which is the point of sharing the derivation rather than restating it.
 * @param updatedAt - ISO timestamp of the card's last update.
 * @param now - Reference instant in epoch ms (defaults to `Date.now()`; injectable for tests).
 * @returns The compact label (e.g. `Updated 2d ago`), or `null` when unknown.
 */
export const formatCardUpdatedAgo = (
  updatedAt: string | null | undefined,
  now: number = Date.now()
): string | null => {
  const elapsed = formatRelativeAgo(updatedAt, now);
  return elapsed === null ? null : `Updated ${elapsed}`;
};

/**
 * Compose the footer's full sentence: the elapsed time, then who edited.
 * @remarks
 * SE-6866. The three outcomes are the whole contract of this function, and
 * they are asymmetric on purpose (see the module note):
 *
 * | `updatedAt` | `editorName` | result                  |
 * | ----------- | ------------ | ----------------------- |
 * | usable      | present      | `Updated 2d ago · Hugo` |
 * | usable      | absent       | `Updated 2d ago`        |
 * | absent      | ANY          | `null` — no row at all  |
 *
 * `editorName` is absent both when the card records no editor and when the id
 * it records resolves to no one — a departed org member, or a viewer whose
 * roster read has not landed. Neither is worth a placeholder: the time-only
 * footer is a complete, true sentence, which `User 4f3a…` would not be.
 * @param updatedAt - ISO timestamp of the card's last update.
 * @param editorName - Display name of the last human editor, or null.
 * @param now - Reference instant in epoch ms (defaults to `Date.now()`; injectable for tests).
 * @returns The footer text, or `null` when the row should not render.
 */
export const formatCardUpdatedFooter = (
  updatedAt: string | null | undefined,
  editorName: string | null | undefined,
  now: number = Date.now()
): string | null => {
  const updated = formatCardUpdatedAgo(updatedAt, now);
  if (updated === null) return null;

  const editor = editorName?.trim();
  return editor ? `${updated}${FOOTER_SEPARATOR}${editor}` : updated;
};
